import { useRef, useState } from 'react';
import ContainerLayout from '@/shared/components/ContainerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Download, FileText, Loader2 } from 'lucide-react';
import { USER_GUIDE_META } from './types';
import { userGuideSections, tableOfContents } from './parseUserGuide';
import { UserGuideContent } from './UserGuideContent';
import { downloadUserGuidePdf } from './downloadUserGuidePdf';
import { toast } from 'sonner';

const UserGuidePage = () => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!contentRef.current) return;

    const cover = document.createElement('div');
    cover.className = 'border-b pb-6 mb-6';
    cover.innerHTML = `
      <h1 style="font-size: 24px; font-weight: bold;">${USER_GUIDE_META.title}</h1>
      <p style="margin-top: 8px; font-size: 16px;">${USER_GUIDE_META.company}</p>
      <p style="font-size: 12px; color: #666;">${USER_GUIDE_META.email}</p>
      <p style="margin-top: 16px; font-size: 12px; color: #666;">Version ${USER_GUIDE_META.version} | ${USER_GUIDE_META.date}</p>
    `;

    setIsDownloading(true);
    contentRef.current.prepend(cover);
    try {
      await downloadUserGuidePdf(contentRef.current);
      toast.success('User guide PDF downloaded successfully');
    } catch {
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      cover.remove();
      setIsDownloading(false);
    }
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <ContainerLayout shouldShowSidebar={false} shouldAllowRegionChange={false} shouldAllowAccountChange={false}>
      <div className="flex flex-1 flex-col gap-4 p-4">
        <div className="glass-panel flex flex-col gap-3 rounded-lg p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <FileText className="h-6 w-6 text-primary" />
              <h1 className="text-3xl font-bold">{USER_GUIDE_META.title}</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              {USER_GUIDE_META.company} · {USER_GUIDE_META.email}
            </p>
            <p className="text-sm text-muted-foreground">
              Version {USER_GUIDE_META.version} | {USER_GUIDE_META.date}
            </p>
          </div>
          <Button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            className="shrink-0"
            data-pdf-exclude
          >
            {isDownloading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Download PDF
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          <Card className="lg:col-span-1 h-fit lg:sticky lg:top-20" data-pdf-exclude>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Contents</CardTitle>
            </CardHeader>
            <CardContent>
              <nav className="flex flex-col">
                {tableOfContents.map((entry) => {
                  const indent = entry.number?.split('.').length ?? 1;
                  const paddingLeft = indent > 1 ? `${(indent - 1) * 12}px` : '6px';
                  const label = entry.number ? `${entry.number} ${entry.title}` : entry.title;

                  return (
                    <Button
                      key={entry.id}
                      variant="ghost"
                      onClick={() => scrollToSection(entry.id)}
                      className="text-left text-sm text-muted-foreground hover:text-foreground hover:underline transition-colors py-2 justify-start gap-2"
                      style={{ paddingLeft }}
                    >
                      {label}
                    </Button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>

          <Card className="lg:col-span-3">
            <CardContent className="pt-6">
              <div ref={contentRef} className="user-guide-pdf-content space-y-8">
                <UserGuideContent sections={userGuideSections} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ContainerLayout>
  );
};

export default UserGuidePage;
